import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  InstantSearch,
  Stats,
  ClearRefinements,
  Pagination,
  Configure,
  connectHits,
  SortBy,
  HitsPerPage,
} from 'react-instantsearch-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSliders,
  faArrowDownWideShort,
  faGrip,
  faXmark,
  faChevronRight,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import {
  faChevronsLeft,
  faChevronsRight,
} from '@fortawesome/pro-solid-svg-icons';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import classNames from 'classnames';

import 'instantsearch.css/themes/reset.css';

import { indexAlgoliaName, topSellerIndex } from 'Redux/ducks/algolia-calls';
import { searchStateFromUrl, navigateToSearchState } from 'Redux/ducks/search';
import { IsChallenger } from 'Redux/ducks/split-test';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import Meta from 'Components/Utility/SEO/Meta';
import NoIndex from 'Components/Utility/SEO/NoIndex';
import searchSetup from 'Redux/ducks/algolia-setup';
import SearchTitle from 'Components/Search/SearchTitle';
import SearchFilters from 'Components/Search/SearchFilters';
import messages from 'Components/Search/SearchMessages';
import CurrentRefinements from 'Components/Search/CurrentRefinements';
import NoResults from 'Components/Search/NoResults';
import useViewport from 'Hooks/useViewport';
import HitCard from 'Components/Cards/HitCard';
import sortByMessages from 'Components/Utility/SortBy/SortByMessages';
import TakeOver from 'Components/Banner/TakeOver';
import HybridSaleContent from './HybridSaleContent/HybridSaleContent';
import CustomFilters from './CustomFilters';

import 'Components/Search/SearchPage.scss';

function Search({ location: { search, pathname } }) {
  const { isAboveXs, isAboveMd } = useViewport();

  const [searchClient, setSearchClient] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(isAboveMd);
  const [modal, setModal] = useState(false);

  const { formatMessage } = useIntl();

  const page = Number(getUrlParameter('page', search) || 1);

  // When there is a sale on
  const liveSales = useSelector(state => state.sales.liveSales);
  const majorSale = liveSales.find(sale => sale.majorSale);
  const saleSlug = get(majorSale, 'saleSlug', '');
  const majorSaleName = get(majorSale, 'displayName', '');

  useEffect(() => {
    searchSetup().then(setSearchClient);
  }, []);

  useEffect(() => {
    if (!isAboveMd) {
      if (modal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'visible';
      }
    }
  }, [modal, isAboveMd]);

  if (!searchClient) {
    return false;
  }

  const topSellerIndexName = topSellerIndex(true);
  const searchState = searchStateFromUrl(search);
  const { multiRange, refinementList, toggle } = searchState;

  const hideCurrentRefinements = (
    (!multiRange || isEmpty(multiRange)) &&
    (!refinementList || isEmpty(refinementList)) &&
    (!toggle || isEmpty(toggle))
  );

  const openFilters = () => {
    if (isAboveMd) setFiltersOpen(!filtersOpen);
    if (!isAboveMd) setModal(!modal);
  };

  // For pages such as On Sale, pass in algolia filters to the config
  let autoFilters = '';
  if (pathname === '/on-sale') {
    autoFilters = 'on_sale: true';
  }
  if (pathname === '/on-sale' && saleSlug) {
    autoFilters = `on_sale: true AND discount_slug: ${saleSlug} OR type:bundle OR type:pick-and-mix`;
  }

  const Hits = connectHits(({ hits }) => hits.map(hit =>
    <div className="ProductListingPage__grid__column" key={hit.product_id}>
      <HitCard
        hit={hit}
        xsSize={12}
        smSize={12}
        mdSize={12}
        lgSize={12}
        sizesOverride="(min-width: 1520px) 307px, (min-width: 1200px) 263px, (min-width: 992px) 298px, (min-width: 768px) 224px, (min-width: 576px) 254px, 50vw"
      />
    </div>,
  ));

  const onSaleMessages = defineMessages({
    title: {
      id: 'onsalesections-search.seotitle',
      defaultMessage: 'PC Game Deals - Games & Steam Keys On Sale',
    },
    desc: {
      id: 'onsale-search.seodesc',
      defaultMessage: 'Top Sellers on great PC, Mac, and Linux games from over 750 top publishers. Whether you\'re looking for the latest, greatest games, or one of our exclusive bundles, we are Fanatical about always bringing you the best deals on the planet.',
    },
  });

  return (
    <>
      {pathname === '/on-sale' && page === 1 &&
        <TakeOver appearsOn="on_sale" />
      }
      <main className="ProductListingPage">
        {pathname === '/on-sale' ?
          <Meta
            title={majorSaleName || formatMessage(onSaleMessages.title)}
            description={formatMessage(onSaleMessages.desc)}
            url="on-sale"
            pageType="CollectionPage"
            brandTitle={!!majorSaleName}
          />
          :
          <>
            <Meta title={formatMessage(messages.title)} />
            <NoIndex allowRender />
          </>
        }

        {pathname === '/on-sale' && page === 1 &&
          <HybridSaleContent
            search={search}
            pathname={pathname}
          />
        }

        <InstantSearch
          searchClient={searchClient}
          indexName={topSellerIndex()}
          searchState={searchState}
          onSearchStateChange={state => navigateToSearchState(location.search, state, pathname)}
        >
          <Configure
            facetingAfterDistinct
            analyticsTags={['userSearch', 'searchPage']}
            filters={autoFilters}
          />
          <>
            <section className="container ProductListingPage__content">
              {pathname !== '/on-sale' &&
                <SearchTitle searchState={searchState} />
              }

              {pathname !== '/on-sale' && IsChallenger('CustomSearchFilters') &&
                <div className={`animated custom-filters-wrapper ${hideCurrentRefinements ? 'visible' : 'd-none'}`}>
                  <CustomFilters />
                </div>
              }

              <div className="ProductListingPage__controls">
                <div className="ProductListingPage__controls__left">
                  <button className="ProductListingPage__filterButton" onClick={() => { openFilters(); }}>
                    <FontAwesomeIcon icon={faSliders} />
                    {!filtersOpen &&
                      <FormattedMessage id="ProductListingPage.button.filter.show" defaultMessage="Filter" />
                    }
                    {filtersOpen &&
                      <FormattedMessage id="ProductListingPage.button.filter.hide" defaultMessage="Hide Filters" />
                    }
                  </button>
                  {isAboveMd &&
                    <div className="ProductListingPage__controls__showing">
                      <FormattedMessage
                        id="ProductListingPage.showing-results"
                        defaultMessage="Showing {number} Products"
                        values={{
                          number: <Stats translations={{ stats: n => `${n.toLocaleString()}` }} />,
                        }}
                      />
                    </div>
                  }
                </div>

                <div className="ProductListingPage__controls__right">
                  <div className="ProductListingPage__filterButton ProductListingPage__controls__right__hitsPerPage">
                    <label hidden className="ProductListingPage__filterButton__label" htmlFor="filter-view">
                      <FormattedMessage id="ProductListingPage.sort.label" defaultMessage="Sort:" />
                    </label>
                    <FontAwesomeIcon icon={faGrip} />
                    <HitsPerPage
                      items={[
                        { value: 24, label: `${formatMessage(messages.results_count, { resultCount: 24 })}` },
                        { value: 36, label: `${formatMessage(messages.results_count, { resultCount: 36 })}` },
                        { value: 48, label: `${formatMessage(messages.results_count, { resultCount: 48 })}` },
                        { value: 60, label: `${formatMessage(messages.results_count, { resultCount: 60 })}` },
                      ]}
                      defaultRefinement={isAboveMd ? 36 : 18}
                      id="filter-view"
                    />
                  </div>

                  <div className="ProductListingPage__filterButton">
                    <label hidden className="ProductListingPage__filterButton__label" htmlFor="filter-sort">
                      <FormattedMessage id="ProductListingPage.sort.label" defaultMessage="Sort:" />
                    </label>
                    <FontAwesomeIcon icon={faArrowDownWideShort} />
                    <SortBy
                      items={[
                        { label: formatMessage(sortByMessages.topSellers), value: topSellerIndexName }, // eslint-disable-line max-len
                        { label: formatMessage(sortByMessages.priceLowHigh), value: `${indexAlgoliaName}_price_asc_unlimited` },
                        { label: formatMessage(sortByMessages.priceHighLow), value: `${indexAlgoliaName}_price_desc` },
                        { label: formatMessage(sortByMessages.alphabetical), value: `${indexAlgoliaName}_name` },
                        { label: formatMessage(sortByMessages.topDiscount), value: `${indexAlgoliaName}_discount` },
                        { label: formatMessage(sortByMessages.latestDeals), value: `${indexAlgoliaName}_latest_deals_unlimited` },
                        { label: formatMessage(sortByMessages.endingSoon), value: `${indexAlgoliaName}_ending_soon` },
                        { label: formatMessage(sortByMessages.mostWanted), value: `${indexAlgoliaName}_most_wanted` },
                        { label: formatMessage(sortByMessages.releaseDate), value: `${indexAlgoliaName}_release_date_desc` },
                        { label: formatMessage(sortByMessages.topRated), value: `${indexAlgoliaName}_top_rated` },
                        { label: formatMessage(sortByMessages.trending), value: `${indexAlgoliaName}_best_price_rank` },
                      ]}
                      defaultRefinement={topSellerIndexName}
                      id="filter-sort"
                    />
                  </div>
                </div>
              </div>

              <div className="ProductListingPage__main">
                <div className={classNames('ProductListingPage__filters', { 'ProductListingPage__filters--closed': !filtersOpen })}>
                  <SearchFilters searchState={searchState} pathname={pathname} />
                </div>

                <div className={classNames('ProductListingPage__grid', { 'ProductListingPage__grid--full': !filtersOpen })}>
                  {!hideCurrentRefinements &&
                    <div className="ProductListingPage__facets">
                      <ClearRefinements
                        translations={{ reset: formatMessage(messages.clear) }}
                      />
                      <CurrentRefinements />
                    </div>
                  }

                  <Hits />

                  <div className="ProductListingPage__grid__pagination">
                    <Pagination
                      padding={2}
                      showLast={isAboveXs}
                      showFirst={isAboveXs}
                      translations={{
                        next: <FontAwesomeIcon icon={faChevronRight} title="Next" />,
                        previous: <FontAwesomeIcon icon={faChevronLeft} title="Previous" />,
                        first: <FontAwesomeIcon icon={faChevronsLeft} title="First" />,
                        last: <FontAwesomeIcon icon={faChevronsRight} title="Last" />,
                      }}
                    />
                  </div>

                  <Stats translations={{
                    stats: n =>
                      <NoResults stats={n} pathname={pathname} />,
                  }}
                  />
                </div>
              </div>
            </section>

            <div className={modal ? 'd-block' : 'd-none'}>
              <div className="modal fade show d-block" aria-modal="true" role="dialog" tabIndex={-1}>
                <div className="modal-dialog SiteModal SiteModal--large SiteModal--dark SiteModal--fullHeightMobile ProductListingFiltersModal">
                  <div className="modal-content">
                    <div className="modal-body">
                      <div className="ProductListingFiltersModal__header h2">
                        <FormattedMessage id="ProductListingPage.modal.header" defaultMessage="Filters" />
                      </div>

                      <div className="ProductListingFiltersModal__filters">
                        <SearchFilters searchState={searchState} pathname={pathname} />
                      </div>

                      <div className="ProductListingFiltersModal__buttons">
                        <ClearRefinements translations={{ reset: formatMessage(messages.clear) }} />

                        <button className="ProductListingPage__filterButton ProductListingPage__filterButton--primary" onClick={() => setModal(!modal)}>
                          <FormattedMessage
                            id="ProductListingPage.modal.button.view"
                            defaultMessage="View {number} results"
                            values={{
                              number: <Stats translations={{ stats: n => `${n.toLocaleString()}` }} />,
                            }}
                          />
                        </button>
                      </div>
                      <div className="SiteModal__closeButton">
                        <button aria-label="close" onClick={() => setModal(!modal)}>
                          <FontAwesomeIcon icon={faXmark} title="Close modal" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {modal &&
                <div className="modal-backdrop fade show" />
              }
            </div>
          </>
        </InstantSearch>
      </main>
    </>
  );
}

Search.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
};

export default Search;
